import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getUniqueValues } from '@repo/utils';

import {
  type ActivitiesType,
  type AttributeOptionsType,
  type DealStatusType,
  type LeadAttributeOptionsType,
  type LeadAttributesType,
  type ProductType,
  type SalesGroupType,
  type TaskType,
  type UsersType,
} from '../queries/referencedConfigService';

// Define the initial state type
export interface ConfigDetailsState {
  users: Record<string, UsersType>;
  leadAttributes: Record<string, LeadAttributesType>;
  activities: Record<string, ActivitiesType>;
  leadStageOptions: AttributeOptionsType[];
  leadSourceOptions: AttributeOptionsType[];
  salesGroups: SalesGroupType[];
  tasks: TaskType[];
  leadAttributeOptions: LeadAttributeOptionsType[];
  products: ProductType[];
  dealStatuses: DealStatusType[];
}

const initialState: ConfigDetailsState = {
  users: {},
  leadAttributes: {},
  activities: {},
  leadStageOptions: [],
  leadSourceOptions: [],
  salesGroups: [],
  tasks: [],
  leadAttributeOptions: [],
  products: [],
  dealStatuses: [],
};

const configDetailsSlice = createSlice({
  name: 'configDetails',
  initialState,
  reducers: {
    setConfigDetails: (
      state,
      action: PayloadAction<Pick<
      ConfigDetailsState,
      'users' | 'leadAttributes' | 'activities' | 'tasks' | 'salesGroups'
      | 'leadAttributeOptions' | 'products' | 'dealStatuses'
      >>,
    ) => {
      const {
        users, leadAttributes, activities, tasks, salesGroups, leadAttributeOptions, products, dealStatuses,
      } = action.payload;
      state.users = { ...state.users, ...users };
      state.leadAttributes = { ...state.leadAttributes, ...leadAttributes };
      state.activities = { ...state.activities, ...activities };
      state.tasks = getUniqueValues([...state.tasks, ...tasks], 'id');
      state.salesGroups = getUniqueValues([...state.salesGroups, ...salesGroups], 'id');
      state.leadAttributeOptions = getUniqueValues([...state.leadAttributeOptions, ...leadAttributeOptions], 'id');
      state.products = getUniqueValues([...state.products, ...(products ?? [])], 'id');
      state.dealStatuses = getUniqueValues([...state.dealStatuses, ...(dealStatuses ?? [])], 'id');
    },
    setUsers: (state, action: PayloadAction<{ data: Record<string, UsersType> }>) => {
      const { data } = action.payload;
      return { ...state, users: { ...state.users, ...data } };
    },
    setLeadAttributes: (state, action: PayloadAction<{ data: Record<string, LeadAttributesType> }>) => {
      const { data } = action.payload;
      return { ...state, leadAttributes: { ...state.leadAttributes, ...data } };
    },
    setActivities: (state, action: PayloadAction<{ data: Record<string, ActivitiesType> }>) => {
      const { data } = action.payload;
      return { ...state, activities: { ...state.activities, ...data } };
    },
    setLeadStageOptions: (state, action: PayloadAction<{ data: AttributeOptionsType[] }>) => {
      const { data } = action.payload;
      return { ...state, leadStageOptions: data };
    },
    setLeadSourceOptions: (state, action: PayloadAction<{ data: AttributeOptionsType[] }>) => {
      const { data } = action.payload;
      return { ...state, leadSourceOptions: data };
    },
    setSalesGroups: (state, action: PayloadAction<{ data: SalesGroupType[] }>) => {
      const { data } = action.payload;
      return { ...state, salesGroups: data };
    },
    setTasks: (state, action: PayloadAction<{ data: TaskType[] }>) => {
      const { data } = action.payload;
      return { ...state, tasks: data };
    },
    setLeadAttributeOptions: (state, action: PayloadAction<{ data: LeadAttributeOptionsType[] }>) => {
      const { data } = action.payload;
      return { ...state, leadAttributeOptions: data };
    },
  },
});

// Export actions
export const {
  setConfigDetails,
  setUsers,
  setLeadAttributes,
  setLeadStageOptions,
  setLeadSourceOptions,
  setSalesGroups,
  setTasks,
  setLeadAttributeOptions,
} = configDetailsSlice.actions;

// Export the reducer
export const configDetailsReducers = configDetailsSlice.reducer;
